import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryEventType, PackagingType, TransportType } from '../../../interfaces/myDataDelivery.interface';

interface Props {
  step: DeliveryEventType;
  stepNumber: number;
}

const EVENT_TYPE_TRANSLATION_MAP: Record<string, string> = {
  RegisterTransfer: 'DELIVERY.EVENT_TYPE_REGISTER_TRANSFER',
  ConfirmOutcome: 'DELIVERY.EVENT_TYPE_CONFIRM_OUTCOME',
  Rejection: 'DELIVERY.EVENT_TYPE_REJECTION',
  ConfirmReturn: 'DELIVERY.EVENT_TYPE_CONFIRM_RETURN',
  RegisterTransferReturn: 'DELIVERY.EVENT_TYPE_REGISTER_TRANSFER_RETURN',
};

const TRANSPORT_TYPE_TRANSLATION_MAP: Record<number, string> = {
  [TransportType.PUBLIC_USE_TRUCK]: 'DELIVERY.TRANSPORT_TYPE_PUBLIC_USE_TRUCK',
  [TransportType.PRIVATE_USE_TRUCK]: 'DELIVERY.TRANSPORT_TYPE_PRIVATE_USE_TRUCK',
  [TransportType.SHIP]: 'DELIVERY.TRANSPORT_TYPE_SHIP',
  [TransportType.TRAIN]: 'DELIVERY.TRANSPORT_TYPE_TRAIN',
  [TransportType.AIRPLANE]: 'DELIVERY.TRANSPORT_TYPE_AIRPLANE',
  [TransportType.OTHER]: 'DELIVERY.TRANSPORT_TYPE_OTHER',
  [TransportType.NONE]: 'DELIVERY.TRANSPORT_TYPE_NONE',
};

const PACKAGING_TYPE_TRANSLATION_MAP: Record<number, string> = {
  [PackagingType.PALLET]: 'DELIVERY.PACKAGING_TYPE_PALLET',
  [PackagingType.BOX]: 'DELIVERY.PACKAGING_TYPE_BOX',
  [PackagingType.CRATE]: 'DELIVERY.PACKAGING_TYPE_CRATE',
  [PackagingType.BARREL]: 'DELIVERY.PACKAGING_TYPE_BARREL',
  [PackagingType.SACK]: 'DELIVERY.PACKAGING_TYPE_SACK',
  [PackagingType.OTHER]: 'DELIVERY.PACKAGING_TYPE_OTHER',
};

export const DeliveryStatusStep = ({ step, stepNumber }: Props) => {
  const { t } = useTranslation();

  const formattedTimestamp = useMemo(() => {
    if (!step.eventTimestamp) return '-';
    try {
      return new Date(step.eventTimestamp).toLocaleString();
    } catch {
      return step.eventTimestamp;
    }
  }, [step.eventTimestamp]);

  const translatedEventType = useMemo(() => {
    if (!step.eventType) return '-';
    const translationKey = EVENT_TYPE_TRANSLATION_MAP[step.eventType];
    return translationKey ? t(translationKey) : step.eventType;
  }, [step.eventType, t]);

  const getTransportDetails = () => {
    if (!step.transportDetails) return null;

    const { vehicleNumber, transportType, carrierVatNumber, pNumber, timeStamp, location } = step.transportDetails;

    const translatedTransportType = TRANSPORT_TYPE_TRANSLATION_MAP[transportType]
      ? t(TRANSPORT_TYPE_TRANSLATION_MAP[transportType])
      : String(transportType);

    const formattedTransportTimestamp = timeStamp
      ? (() => {
          try {
            return new Date(timeStamp).toLocaleString();
          } catch {
            return timeStamp;
          }
        })()
      : null;

    return (
      <div className="margin-t-8 p-l-8" style={{ borderLeft: '2px solid var(--ion-color-medium)' }}>
        <div className="boldText4 margin-b-4">{t('DELIVERY.TRANSPORT_DETAILS')}</div>
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.TRANSPORT_VEHICLE_NUMBER')}:</div>
          <div className="normalText4">{vehicleNumber || '-'}</div>
        </div>
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.TRANSPORT_TYPE')}:</div>
          <div className="normalText4">{translatedTransportType}</div>
        </div>
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.TRANSPORT_CARRIER_VAT')}:</div>
          <div className="normalText4">{carrierVatNumber || '-'}</div>
        </div>
        {pNumber && (
          <div className="flex-start-center-row-nowrap margin-b-4">
            <div className="boldText4 padding-r-6">{t('DELIVERY.TRANSPORT_P_NUMBER')}:</div>
            <div className="normalText4">{pNumber}</div>
          </div>
        )}
        {formattedTransportTimestamp && (
          <div className="flex-start-center-row-nowrap margin-b-4">
            <div className="boldText4 padding-r-6">{t('DELIVERY.DELIVERY_STEP_TIMESTAMP')}:</div>
            <div className="normalText4">{formattedTransportTimestamp}</div>
          </div>
        )}
        {location && (
          <div className="flex-start-center-row-nowrap margin-b-4">
            <div className="boldText4 padding-r-6">{t('DELIVERY.TRANSPORT_LOCATION')}:</div>
            <div className="normalText4">
              {location.latitude}, {location.longitude}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getOutcomeDetails = () => {
    if (!step.outcomeDetails) return null;

    const { outcome, deliveredWithoutRecipient, deliveredPackaging } = step.outcomeDetails;

    return (
      <div className="margin-t-8 p-l-8" style={{ borderLeft: '2px solid var(--ion-color-medium)' }}>
        <div className="boldText4 margin-b-4">{t('DELIVERY.OUTCOME_DETAILS')}</div>
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.OUTCOME_RESULT')}:</div>
          <div className="normalText4">{t(`DELIVERY.OUTCOME_${outcome}`)}</div>
        </div>
        {deliveredWithoutRecipient !== undefined && (
          <div className="flex-start-center-row-nowrap margin-b-4">
            <div className="boldText4 padding-r-6">{t('DELIVERY.OUTCOME_WITHOUT_RECIPIENT')}:</div>
            <div className="normalText4">{deliveredWithoutRecipient ? t('DELIVERY.YES') : t('DELIVERY.NO')}</div>
          </div>
        )}
        {deliveredPackaging && deliveredPackaging.length > 0 && (
          <div className="margin-t-4">
            <div className="boldText4 margin-b-4">{t('DELIVERY.OUTCOME_PACKAGING')}:</div>
            {deliveredPackaging.map((pkg, index) => {
              const translatedPackagingType = PACKAGING_TYPE_TRANSLATION_MAP[pkg.packagingType]
                ? t(PACKAGING_TYPE_TRANSLATION_MAP[pkg.packagingType])
                : String(pkg.packagingType);

              return (
                <div key={index} className="margin-b-4 flex-start-start-row-wrap gap-6 p-l-6">
                  <div className="flex-start-center-row-nowrap">
                    <div className="boldText4 padding-r-6">
                      {index + 1}. {t('DELIVERY.PACKAGING_TYPE_LABEL')}:
                    </div>
                    <div className="normalText4">{translatedPackagingType}</div>
                  </div>
                  <div className="flex-start-center-row-nowrap">
                    <div className="boldText4 padding-r-6">{t('DELIVERY.PACKAGING_QUANTITY')}:</div>
                    <div className="normalText4">{pkg.quantity}</div>
                  </div>
                  {pkg.otherPackagingTypeTitle && (
                    <div className="flex-start-center-row-nowrap">
                      <div className="normalText4">({pkg.otherPackagingTypeTitle})</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const getRejectionDetails = () => {
    if (!step.rejectionDetails) return null;

    return (
      <div className="margin-t-8 p-l-8" style={{ borderLeft: '2px solid var(--ion-color-medium)' }}>
        <div className="boldText4 margin-b-4">{t('DELIVERY.REJECTION_DETAILS')}</div>
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.REJECTION_REASON')}:</div>
          <div className="normalText4">{step.rejectionDetails.reason || '-'}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="width-100 simpleBorder p-12">
      <div className="boldText5 margin-b-8">
        {t('DELIVERY.DELIVERY_STEP')} #{stepNumber}
      </div>
      <div className="flex-start-center-row-nowrap margin-b-4">
        <div className="boldText4 padding-r-6">{t('DELIVERY.DELIVERY_STEP_EVENT_TYPE')}:</div>
        <div className="normalText4">{translatedEventType}</div>
      </div>
      <div className="flex-start-center-row-nowrap margin-b-4">
        <div className="boldText4 padding-r-6">{t('DELIVERY.DELIVERY_STEP_TIMESTAMP')}:</div>
        <div className="normalText4">{formattedTimestamp}</div>
      </div>
      <div className="flex-start-center-row-nowrap margin-b-4">
        <div className="boldText4 padding-r-6">{t('DELIVERY.DELIVERY_STEP_ACTOR_VAT')}:</div>
        <div className="normalText4">{step.actorVat || '-'}</div>
      </div>
      {step.mark && (
        <div className="flex-start-center-row-nowrap margin-b-4">
          <div className="boldText4 padding-r-6">{t('DELIVERY.NEW_DELIVERY_MARK_PLACEHOLDER')}:</div>
          <div className="normalText4">{step.mark}</div>
        </div>
      )}
      {getTransportDetails()}
      {getOutcomeDetails()}
      {getRejectionDetails()}
    </div>
  );
};
